import React from 'react';
import { Building2, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { SortableHeader } from '../../utils/tableSort';
import PartyCard from './PartyCard';
import AppSelect from '../shared/AppSelect';
import {
  PARTIES_THEME,
  btnDangerSm,
  btnSecondarySm,
  partyTypeBadgeClass,
  partyTypeLabel,
} from './partiesTheme';

const QUICK_TYPES = [
  { value: 'All', label: 'All' },
  { value: 'Buyer', label: 'Customers' },
  { value: 'Supplier', label: 'Suppliers' },
];

const GST_OPTIONS = [
  { value: 'All', label: 'All GST' },
  { value: 'Regular', label: 'Regular' },
  { value: 'Composition', label: 'Composition' },
  { value: 'Unregistered', label: 'Unregistered' },
];

const BALANCE_OPTIONS = [
  { value: 'All', label: 'All balances' },
  { value: 'Receivable', label: 'Receivable' },
  { value: 'Payable', label: 'Payable' },
  { value: 'No Balance', label: 'Zero' },
];

const selectFieldCls =
  'appearance-none w-full rounded-lg border border-slate-200 bg-white pl-2.5 pr-9 py-2 text-sm text-slate-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-500';

function FilterSelect({ value, onChange, options, ariaLabel, minWidth = '7.5rem', sheetTitle }) {
  return (
    <div className="relative shrink-0" style={{ minWidth }}>
      <AppSelect
        value={value}
        onChange={onChange}
        options={options}
        aria-label={ariaLabel}
        sheetTitle={sheetTitle || ariaLabel}
        triggerClassName={`${selectFieldCls} flex items-center justify-between gap-1`}
        className={selectFieldCls}
      />
    </div>
  );
}

export default function PartyListPanel({
  embedded = false,
  splitLayout = false,
  listOnly = false,
  t,
  partiesList,
  filteredParties,
  tableRows,
  sortConfig,
  handleSort,
  searchTerm,
  filterType,
  filterGstType,
  filterBalance,
  onFilterChange,
  onClearFilters,
  onQuickType,
  selectedForDeletion,
  onToggleSelect,
  onSelectAllPage,
  onEdit,
  onDelete,
  onSelect,
  onBulkDeleteClick,
  isBulkDeleting,
  onAddClick,
  matchedCount,
  totalCount,
}) {
  const theme = PARTIES_THEME;
  const rows = tableRows ?? filteredParties ?? [];
  const displayMatched = matchedCount ?? filteredParties.length;
  const displayTotal = totalCount ?? partiesList.length;
  const allPageSelected =
    rows.length > 0 && rows.every((p) => selectedForDeletion.has(p.id));
  const hasFilters =
    searchTerm || filterType !== 'All' || filterGstType !== 'All' || filterBalance !== 'All';

  const patchFilters = (patch) => {
    onFilterChange({
      search: searchTerm,
      type: filterType,
      gstType: filterGstType,
      balance: filterBalance,
      ...patch,
    });
  };

  const unifiedToolbar = (
    <div
      className={`flex flex-wrap items-center gap-2 px-3 py-2 border-b ${theme.borderSoft} ${theme.filterBarBg} shrink-0`}
    >
      <div className="flex items-center gap-1.5 shrink-0">
        {QUICK_TYPES.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => onQuickType(value)}
            className={`px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${
              filterType === value ? theme.filterPillActive : theme.filterPillIdle
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="relative flex-1 min-w-[10rem] max-w-xl">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          aria-hidden
        />
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => patchFilters({ search: e.target.value })}
          placeholder={theme.searchPlaceholder}
          className={`w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm ${theme.filterFocus}`}
          aria-label="Search parties"
        />
      </div>

      <FilterSelect
        value={filterGstType}
        onChange={(e) => patchFilters({ gstType: e.target.value })}
        options={GST_OPTIONS}
        ariaLabel="Filter by GST type"
        minWidth="6.5rem"
      />

      <FilterSelect
        value={filterBalance}
        onChange={(e) => patchFilters({ balance: e.target.value })}
        options={BALANCE_OPTIONS}
        ariaLabel="Filter by balance"
        minWidth="9.5rem"
      />

      {hasFilters && (
        <button
          type="button"
          onClick={onClearFilters}
          className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900"
        >
          <X className="h-3.5 w-3.5" />
          Clear
        </button>
      )}

      {selectedForDeletion.size > 0 && (
        <button
          type="button"
          onClick={onBulkDeleteClick}
          disabled={isBulkDeleting}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Delete ({selectedForDeletion.size})
        </button>
      )}

      <span className={`ml-auto text-xs tabular-nums whitespace-nowrap ${theme.mutedText}`}>
        {displayMatched} shown
        {displayMatched !== displayTotal ? ` · ${displayTotal} total` : ''}
      </span>
    </div>
  );

  const emptyState = (
    <div
      className={`mx-4 my-10 rounded-xl border border-dashed ${theme.emptyBorder} ${theme.emptyBg} p-10 text-center`}
    >
      <Building2 className={`h-12 w-12 mx-auto mb-3 ${theme.emptyIcon}`} aria-hidden />
      <p className={`text-base font-medium ${theme.emptyTitle}`}>No parties yet</p>
      <button
        type="button"
        onClick={onAddClick}
        className={`mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white rounded-xl bg-gradient-to-r ${theme.btnAddParty} transition-all hover:scale-[1.02] active:scale-[0.98]`}
      >
        <Plus className="h-4 w-4" />
        Add first party
      </button>
    </div>
  );

  const desktopTable = (
    <table id="parties-table" className={`w-full table-fixed divide-y ${theme.tableDivide} text-sm`}>
      <colgroup>
        <col className="w-10" />
        <col className="w-[22%]" />
        <col className="w-[9%]" />
        <col className="w-[11%]" />
        <col className="w-[14%]" />
        <col className="w-[10%]" />
        <col className="w-[12%]" />
        <col className="w-[8%]" />
        <col className="w-[10%]" />
        <col className="w-[4%]" />
      </colgroup>
      <thead className={theme.tableHead}>
        <tr>
          <th className="px-3 py-2.5">
            <input
              type="checkbox"
              checked={allPageSelected}
              onChange={onSelectAllPage}
              className={`rounded ${theme.checkboxBorder} ${theme.checkbox}`}
              aria-label="Select all on page"
            />
          </th>
          <SortableHeader
            columnKey="firmName"
            label={t('firmName')}
            onSort={handleSort}
            sortConfig={sortConfig}
            className={`!py-2.5 !px-3 text-xs font-semibold uppercase tracking-wider ${theme.tableHeadText}`}
          />
          <SortableHeader
            columnKey="partyType"
            label={t('type')}
            onSort={handleSort}
            sortConfig={sortConfig}
            className={`!py-2.5 !px-3 text-xs font-semibold uppercase tracking-wider ${theme.tableHeadText}`}
          />
          <SortableHeader
            columnKey="contact"
            label={t('contact')}
            onSort={handleSort}
            sortConfig={sortConfig}
            className={`!py-2.5 !px-3 text-xs font-semibold uppercase tracking-wider ${theme.tableHeadText}`}
          />
          <SortableHeader
            columnKey="email"
            label={t('email')}
            onSort={handleSort}
            sortConfig={sortConfig}
            className={`!py-2.5 !px-3 text-xs font-semibold uppercase tracking-wider ${theme.tableHeadText}`}
          />
          <SortableHeader
            columnKey="gstin"
            label="GSTIN"
            onSort={handleSort}
            sortConfig={sortConfig}
            className={`!py-2.5 !px-3 text-xs font-semibold uppercase tracking-wider ${theme.tableHeadText}`}
          />
          <SortableHeader
            columnKey="city"
            label="City"
            onSort={handleSort}
            sortConfig={sortConfig}
            className={`!py-2.5 !px-3 text-xs font-semibold uppercase tracking-wider ${theme.tableHeadText}`}
          />
          <SortableHeader
            columnKey="openingBalance"
            label={t('openingBalance')}
            onSort={handleSort}
            sortConfig={sortConfig}
            className={`!py-2.5 !px-3 text-xs font-semibold uppercase tracking-wider ${theme.tableHeadText}`}
          />
          <SortableHeader
            columnKey="gstType"
            label="GST"
            onSort={handleSort}
            sortConfig={sortConfig}
            className={`!py-2.5 !px-3 text-xs font-semibold uppercase tracking-wider ${theme.tableHeadText}`}
          />
          <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
            Actions
          </th>
        </tr>
      </thead>
      <tbody className={`bg-white divide-y ${theme.tbodyDivide}`}>
        {rows.map((party) => {
          const balance = Number(party.openingBalance) || 0;
          return (
            <tr
              key={party.id}
              className={`${theme.hoverRow} cursor-pointer`}
              onClick={() => onSelect(party)}
            >
              <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedForDeletion.has(party.id)}
                  onChange={() => onToggleSelect(party.id)}
                  className={`rounded ${theme.checkboxBorder} ${theme.checkbox}`}
                />
              </td>
              <td className="px-3 py-2.5 min-w-0">
                <div className="font-medium text-slate-900 truncate">{party.firmName}</div>
                {party.personName && (
                  <div className="text-xs text-slate-500 truncate">{party.personName}</div>
                )}
              </td>
              <td className="px-3 py-2.5">
                <span
                  className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ring-1 ${partyTypeBadgeClass(party.partyType)}`}
                >
                  {partyTypeLabel(party.partyType)}
                </span>
              </td>
              <td className="px-3 py-2.5 text-slate-700 truncate">{party.contact || '—'}</td>
              <td className="px-3 py-2.5 text-slate-600 truncate text-xs">{party.email || '—'}</td>
              <td className="px-3 py-2.5 text-slate-600 truncate text-xs font-mono">
                {party.gstin || '—'}
              </td>
              <td className="px-3 py-2.5 text-slate-600 truncate text-xs">
                {[party.city, party.stateName].filter(Boolean).join(', ') || '—'}
              </td>
              <td className="px-3 py-2.5 whitespace-nowrap tabular-nums text-right">
                <span
                  className={`font-medium ${
                    balance > 0 ? 'text-emerald-600' : balance < 0 ? 'text-red-600' : 'text-slate-400'
                  }`}
                >
                  {balance > 0 ? '+' : ''}
                  {balance.toLocaleString('en-IN')}
                </span>
              </td>
              <td className="px-3 py-2.5 text-slate-600 text-xs">{party.gstType || '—'}</td>
              <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => onEdit(party)}
                    className={btnSecondarySm}
                    title="Edit"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(party.id)}
                    className={btnDangerSm}
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const mobileList = (
    <div className="px-2 py-2">
      {rows.map((party) => (
        <PartyCard
          key={party.id}
          party={party}
          onSelect={onSelect}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );

  const listBody =
    partiesList.length === 0 ? (
      emptyState
    ) : filteredParties.length === 0 ? (
      <div className={`p-10 text-center text-sm ${theme.mutedText}`}>
        <Search className={`h-8 w-8 mx-auto mb-2 ${theme.emptyIcon}`} aria-hidden />
        No parties match your filters.
        <button
          type="button"
          onClick={onClearFilters}
          className="block mx-auto mt-2 text-teal-700 text-sm font-medium hover:underline"
        >
          Clear filters
        </button>
      </div>
    ) : (
      <div className="flex flex-col min-h-0 flex-1 w-full">
        <div
          className="hidden lg:block flex-1 min-h-0 overflow-y-auto overscroll-y-contain"
          id="parties-table-scroll"
          role="region"
          aria-label="Parties list"
          tabIndex={0}
        >
          {desktopTable}
        </div>
        <div
          className="lg:hidden flex-1 min-h-0 overflow-y-auto overscroll-y-contain"
          id="parties-list"
        >
          {mobileList}
        </div>
      </div>
    );

  const listFooter =
    partiesList.length > 0 && filteredParties.length > 0 ? (
      <div
        className={`shrink-0 border-t ${theme.borderSoft} bg-slate-50 px-3 py-1.5 flex items-center justify-between text-xs ${theme.mutedText}`}
        id="parties-list-footer"
      >
        <span>
          <span className="font-medium text-slate-700">{rows.length}</span> parties in list
          {rows.length > 10 ? ' — scroll to see more' : ''}
        </span>
        {selectedForDeletion.size > 0 && (
          <span className="text-slate-600">{selectedForDeletion.size} selected</span>
        )}
      </div>
    ) : null;

  if (listOnly || (embedded && splitLayout)) {
    return (
      <div className="flex flex-col flex-1 min-h-0 overflow-visible lg:overflow-hidden w-full h-full">
        {unifiedToolbar}
        {listBody}
        {listFooter}
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col flex-1 min-h-0 rounded-xl border ${theme.cardBorder} bg-white ${theme.cardShadow} overflow-hidden w-full`}
    >
      {unifiedToolbar}
      {listBody}
      {listFooter}
    </div>
  );
}
